/**
 * Memoized views of the strategies slice for the Strategies chart. Kept out of
 * the slice file so it can type against RootState without an import cycle.
 *
 * Memoization matters here: `Chart` is large and re-renders on any selector
 * that returns a fresh array, so these only produce new references when the
 * underlying book / trades / selection actually change.
 */
import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "./store";
import type { StrategyTrade, TradingDeal, TradingOrder, TradingPosition } from "@/api/types";

export interface StrategyMeta {
  id: string;
  name: string;
  label: string;
  color: string;
}

const selectBook = (s: RootState) => s.strategies.book;
const selectPicked = (s: RootState) => s.strategies.selectedIds;
const selectFocused = (s: RootState) => s.strategies.focusedId;
// Focus mode isolates one strategy: every chart/list view narrows to it, and
// clearing focus brings the whole selection back. Memoized — returning a fresh
// array on each call would re-render the chart on every store update.
const selectSelected = createSelector(
  [selectPicked, selectFocused],
  (ids, focused): string[] => (focused ? [focused] : ids),
);
const selectList = (s: RootState) => s.strategies.strategies;
const selectHistory = (s: RootState) => s.strategies.history;

export const selectStrategyPositions = createSelector(
  [selectBook, selectSelected],
  (book, ids): TradingPosition[] => ids.flatMap((id) => book[id]?.positions ?? []),
);

export const selectStrategyOrders = createSelector(
  [selectBook, selectSelected],
  (book, ids): TradingOrder[] => ids.flatMap((id) => book[id]?.orders ?? []),
);

/** magic → strategy identity, for colouring and labelling chart levels. */
export const selectStrategyMetaByMagic = createSelector(
  [selectList],
  (list): Map<number, StrategyMeta> =>
    new Map(list.map((s) => [s.magic, { id: s.id, name: s.name, label: s.label, color: s.color }])),
);

/** Selected strategies' trades as entry/exit deal rows, so the chart's existing
 *  closed-trade marker code (built for MT5 deals) draws them unchanged. */
export const selectStrategyHistoryDeals = createSelector(
  [selectHistory, selectSelected],
  (history, ids): TradingDeal[] => {
    const out: TradingDeal[] = [];
    for (const id of ids) {
      for (const t of history[id] ?? []) out.push(...tradeToDeals(t));
    }
    return out;
  },
);

function tradeToDeals(t: StrategyTrade): TradingDeal[] {
  const isBuy = t.side === "buy";
  const entry: TradingDeal = {
    ticket: t.position_id * 10,
    order: 0,
    position_id: t.position_id,
    time: t.open_time,
    type: isBuy ? 0 : 1,
    type_label: t.side,
    entry: 0,
    entry_label: "in",
    symbol: t.symbol,
    volume: t.volume,
    price: t.open_price,
    commission: 0,
    swap: 0,
    profit: 0,
    fee: 0,
    magic: t.magic,
    comment: t.setup,
  };
  if (!t.closed || t.close_time == null || t.close_price == null) return [entry];
  const exit: TradingDeal = {
    ...entry,
    ticket: t.position_id * 10 + 1,
    time: t.close_time,
    type: isBuy ? 1 : 0,
    type_label: isBuy ? "sell" : "buy",
    entry: 1,
    entry_label: "out",
    price: t.close_price,
    profit: t.profit,
    comment: t.exit_reason,
  };
  return [entry, exit];
}
